import type { ReactNode } from "react";
import type { QuoteRow } from "@/hooks/useQuotes";
import { DraftNumberInput } from "@/components/quotes/QuoteLineItemRow";
import { parsePrice } from "@/components/quotes/lineItemNumbers";
import { isPickup } from "@/types/quotes";
import { formatMoney, taxLabel } from "./drawerLogic";
import type { QuoteDrawerDraft } from "./useQuoteDrawerDraft";

interface TotalsSectionProps {
  quote: QuoteRow;
  draft: Pick<QuoteDrawerDraft, "editing" | "fields" | "set" | "toggleGratuity" | "totals">;
  /** getDocumentLabel's word: shown read-only (Frank's decision). */
  docLabel: string;
}

const box = "h-7 w-24 px-2 text-right tabular-nums";
const pctBox = "h-7 w-16 px-2 text-right tabular-nums";

function Row({ label, value, sub }: { label: ReactNode; value: ReactNode; sub?: boolean }) {
  const cls = sub ? "text-[12.5px] text-muted-foreground" : undefined;
  return (
    <>
      <span className={cls}>{label}</span>
      <span className={cls}>{value}</span>
    </>
  );
}

/** The totals grid, view or edit, and "Shown to client as …" (#1559). */
export function TotalsSection({ quote, draft, docLabel }: TotalsSectionProps) {
  const { editing: ed, fields: f, set, totals: t } = draft;
  const pickup = isPickup(f.serviceType);
  const money = (field: "discount" | "delivery", label: string) => (
    <DraftNumberInput aria-label={label} placeholder="$0" idleText={f[field] || ""} parse={parsePrice} fallback={0}
      onValue={(n) => set(field, n)} className={box} />
  );
  const pct = (field: "gratuityPercent" | "depositPercent", label: string) => (
    <span className="inline-flex items-center gap-1.5">
      <DraftNumberInput aria-label={label} idleText={f[field]} parse={parsePrice} fallback={0}
        onValue={(n) => set(field, n)} className={pctBox} />%
    </span>
  );
  const deposit = (t.total * (Number(f.depositPercent) || 0)) / 100;
  return (
    <section data-section="totals" className="rounded-[10px] border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Totals</h3>
        <span className="text-[12.5px] text-muted-foreground">Shown to client as <b className="font-semibold text-foreground">{docLabel}</b></span>
      </div>
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-3.5 gap-y-1.5 px-3.5 py-3 text-[14px] tabular-nums">
        <Row label="Subtotal" value={formatMoney(t.subtotal)} />
        <Row label="Discount" value={ed ? money("discount", "Discount") : t.discount_amount ? `−${formatMoney(t.discount_amount)}` : "None"} />
        {pickup ? (
          <>
            <span>Delivery</span>
            <span className="text-[12.5px] text-muted-foreground">None, it's a pickup</span>
          </>
        ) : (
          <Row label="Delivery" value={ed ? money("delivery", "Delivery") : t.delivery_amount ? formatMoney(t.delivery_amount) : "None"} />
        )}
        {t.service_charge_amount > 0 && (
          <Row
            label={<>Service charge <span className="text-[11px] font-semibold text-muted-foreground">· from Square, read-only</span></>}
            value={formatMoney(t.service_charge_amount)}
          />
        )}
        <span>
          {ed ? (
            <label className="inline-flex items-center gap-[7px]">
              <input type="checkbox" checked={f.gratuityOn} onChange={(e) => draft.toggleGratuity(e.target.checked)} className="h-4 w-4 accent-primary" />
              Gratuity
            </label>
          ) : "Gratuity"}
          {f.gratuityOn && (
            <span className="ml-1 text-[12.5px] text-muted-foreground">{ed ? pct("gratuityPercent", "Gratuity percent") : `${f.gratuityPercent}%`}</span>
          )}
        </span>
        <span>{f.gratuityOn ? formatMoney(t.gratuity_amount) : "None"}</span>
        <Row label={taxLabel(ed ? 13 : quote.tax_rate)} value={formatMoney(t.tax_amount)} />
        <span className="border-t pt-2 text-[16px] font-bold">Total</span>
        <span className="border-t pt-2 text-[16px] font-bold">{formatMoney(t.total)}</span>
        {(f.depositPercent > 0 || ed) && (
          <Row sub label={<>Deposit {ed ? pct("depositPercent", "Deposit percent") : `${f.depositPercent}%`}</>} value={`${formatMoney(deposit)} deposit`} />
        )}
      </div>
    </section>
  );
}
